import React from 'react';
import { GlassCard } from './ui/GlassCard';
import { motion } from 'framer-motion';
import { Target, Zap, AlertTriangle, CheckCircle2, XCircle, BarChart3, TrendingUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
export interface ResultsData {
  winProbA: number;
  winProbB: number;
  drawProb: number;
  impliedProbA: number;
  impliedProbB: number;
  adjScoreA: number;
  adjScoreB: number;
  valueIndicator: number; // 1 to 100
  recommendation: 'high' | 'medium' | 'avoid' | null;
}
interface RightPanelProps {
  results: ResultsData | null;
  teamA: string;
  teamB: string;
}
export function RightPanel({
  results,
  teamA,
  teamB
}: RightPanelProps) {
  const tA = teamA || 'Equipo A';
  const tB = teamB || 'Equipo B';
  if (!results) {
    return <div className="h-full min-h-[600px] flex flex-col items-center justify-center text-slate-500 border border-dashed border-slate-700/50 rounded-2xl bg-[#131B2F]/30 p-8 text-center" data-id="element-127">
        <BarChart3 className="w-16 h-16 mb-4 opacity-20" data-id="element-128" />
        <h3 className="text-xl font-medium text-slate-400 mb-2" data-id="element-129">
          Esperando Inteligencia
        </h3>
        <p className="max-w-md text-sm" data-id="element-130">
          Ingresa los parámetros del partido y haz clic en "Analizar Apuesta"
          para generar modelos predictivos e indicadores de valor.
        </p>
      </div>;
  }
  const getRecConfig = (rec: string | null) => {
    switch (rec) {
      case 'high':
        return {
          color: 'text-emerald-400',
          bg: 'bg-emerald-500/10',
          border: 'border-emerald-500/20',
          icon: <CheckCircle2 className="w-8 h-8 text-emerald-400" data-id="element-131" />,
          title: '🔥 Oportunidad de Alto Valor',
          desc: 'Ventaja significativa detectada contra las cuotas actuales del mercado. Las condiciones favorables se alinean con el rendimiento histórico.'
        };
      case 'medium':
        return {
          color: 'text-amber-400',
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/20',
          icon: <AlertTriangle className="w-8 h-8 text-amber-400" data-id="element-132" />,
          title: '⚠️ Riesgo Medio',
          desc: 'Valor marginal identificado. Procede con precaución y considera reducir el tamaño estándar de la unidad.'
        };
      case 'avoid':
        return {
          color: 'text-rose-400',
          bg: 'bg-rose-500/10',
          border: 'border-rose-500/20',
          icon: <XCircle className="w-8 h-8 text-rose-400" data-id="element-133" />,
          title: '❌ Evitar Apuesta',
          desc: 'Valor esperado negativo. Las cuotas del mercado son excesivamente optimistas dados los datos actuales de lesiones y forma.'
        };
      default:
        return null;
    }
  };
  const recConfig = getRecConfig(results.recommendation);
  const edgeA = (results.winProbA - results.impliedProbA).toFixed(1);
  const edgeB = (results.winProbB - results.impliedProbB).toFixed(1);
  const hasPositiveEdge = parseFloat(edgeA) > 0 || parseFloat(edgeB) > 0;
  return <div className="flex flex-col gap-6 w-full h-full" data-id="element-134">
      {/* Win Probability */}
      <GlassCard delay={0.1} className="relative overflow-hidden" data-id="element-135">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" data-id="element-136" />

        <div className="flex items-center gap-2 mb-8" data-id="element-137">
          <Target className="w-5 h-5 text-blue-400" data-id="element-138" />
          <h2 className="text-lg font-semibold text-white" data-id="element-139">
            Modelo de Probabilidad de Victoria
          </h2>
        </div>

        <div className="flex items-end justify-between mb-4" data-id="element-140">
          <div data-id="element-141">
            <div className="text-4xl font-bold text-white tracking-tight" data-id="element-142">
              {results.winProbA}%
            </div>
            <div className="text-sm text-slate-400 mt-1" data-id="element-143">{tA}</div>
          </div>
          <div className="text-center pb-1" data-id="element-144">
            <div className="text-xl font-semibold text-slate-300" data-id="element-145">
              {results.drawProb}%
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1" data-id="element-146">
              Empate
            </div>
          </div>
          <div className="text-right" data-id="element-147">
            <div className="text-4xl font-bold text-white tracking-tight" data-id="element-148">
              {results.winProbB}%
            </div>
            <div className="text-sm text-slate-400 mt-1" data-id="element-149">{tB}</div>
          </div>
        </div>

        {/* Probability Bar */}
        <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden flex" data-id="element-150">
          <motion.div initial={{
          width: 0
        }} animate={{
          width: `${results.winProbA}%`
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: 'easeOut'
        }} className="h-full bg-blue-500" data-id="element-151" />
          <motion.div initial={{
          width: 0
        }} animate={{
          width: `${results.drawProb}%`
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: 'easeOut'
        }} className="h-full bg-slate-500" data-id="element-152" />
          <motion.div initial={{
          width: 0
        }} animate={{
          width: `${results.winProbB}%`
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: 'easeOut'
        }} className="h-full bg-emerald-500" data-id="element-153" />
        </div>
      </GlassCard>

      <div className="grid grid-cols-2 gap-6" data-id="element-154">
        {/* Adjusted Probability Score */}
        <GlassCard delay={0.2} data-id="element-155">
          <div className="flex items-center gap-2 mb-4" data-id="element-156">
            <Zap className="w-4 h-4 text-amber-400" data-id="element-157" />
            <h3 className="text-sm font-medium text-slate-300" data-id="element-158">
              Puntuación Ajustada
            </h3>
          </div>
          <div className="space-y-4" data-id="element-159">
            <div data-id="element-160">
              <div className="flex justify-between text-sm mb-1" data-id="element-161">
                <span className="text-slate-400" data-id="element-162">{tA}</span>
                <span className="font-medium text-white" data-id="element-163">
                  {results.adjScoreA}
                </span>
              </div>
              <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden" data-id="element-164">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: `${Math.min(100, results.adjScoreA)}%`
              }} className="h-full bg-amber-400/80" data-id="element-165" />
              </div>
            </div>
            <div data-id="element-166">
              <div className="flex justify-between text-sm mb-1" data-id="element-167">
                <span className="text-slate-400" data-id="element-168">{tB}</span>
                <span className="font-medium text-white" data-id="element-169">
                  {results.adjScoreB}
                </span>
              </div>
              <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden" data-id="element-170">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: `${Math.min(100, results.adjScoreB)}%`
              }} className="h-full bg-amber-400/80" data-id="element-171" />
              </div>
            </div>
          </div>
        </GlassCard>

        {/* Value Indicator */}
        <GlassCard delay={0.3} className="flex flex-col justify-center items-center text-center" data-id="element-172">
          <h3 className="text-sm font-medium text-slate-300 mb-2" data-id="element-173">
            Indicador de Valor
          </h3>
          <div className="relative flex items-center justify-center w-24 h-24" data-id="element-174">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100" data-id="element-175">
              <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-800" data-id="element-176" />
              <motion.circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray="251.2" initial={{
              strokeDashoffset: 251.2
            }} animate={{
              strokeDashoffset: 251.2 - 251.2 * results.valueIndicator / 100
            }} transition={{
              duration: 1.5,
              delay: 0.4,
              ease: 'easeOut'
            }} className={`${results.valueIndicator > 70 ? 'text-emerald-400' : results.valueIndicator > 40 ? 'text-amber-400' : 'text-rose-400'}`} strokeLinecap="round" data-id="element-177" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center flex-col" data-id="element-178">
              <span className="text-2xl font-bold text-white" data-id="element-179">
                {results.valueIndicator}
              </span>
            </div>
          </div>
          <span className="text-xs text-slate-500 mt-2 uppercase tracking-wider" data-id="element-180">
            Puntuación de Ventaja
          </span>
        </GlassCard>
      </div>

      {/* Value Opportunity Indicator */}
      <GlassCard delay={0.4} data-id="element-181">
        <div className="flex items-center justify-between mb-6" data-id="element-182">
          <div className="flex items-center gap-2" data-id="element-183">
            <TrendingUp className="w-5 h-5 text-indigo-400" data-id="element-184" />
            <h2 className="text-lg font-semibold text-white" data-id="element-185">
              Indicador de Oportunidad de Valor
            </h2>
          </div>
          {hasPositiveEdge ? <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-186">
              Valor Positivo Detectado
            </span> : <span className="px-3 py-1 bg-slate-800 text-slate-400 border border-slate-700 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-187">
              Sin Valor
            </span>}
        </div>

        <div className="grid grid-cols-2 gap-6" data-id="element-188">
          {/* Team A Edge */}
          <div className="space-y-3" data-id="element-189">
            <div className="text-sm font-medium text-slate-300 border-b border-white/5 pb-2" data-id="element-190">
              {tA}
            </div>
            <div className="flex justify-between text-sm" data-id="element-191">
              <span className="text-slate-400" data-id="element-192">Prob. Calculada:</span>
              <span className="text-white font-medium" data-id="element-193">
                {results.winProbA}%
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-194">
              <span className="text-slate-400" data-id="element-195">Prob. Implícita:</span>
              <span className="text-white font-medium" data-id="element-196">
                {results.impliedProbA}%
              </span>
            </div>
            <div className={`flex items-center gap-1 text-sm font-bold mt-2 pt-2 border-t border-white/5 ${parseFloat(edgeA) > 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-id="element-197">
              {parseFloat(edgeA) > 0 ? <ArrowUpRight className="w-4 h-4" data-id="element-198" /> : <ArrowDownRight className="w-4 h-4" data-id="element-199" />}
              {parseFloat(edgeA) > 0 ? `+${edgeA}% Ventaja` : `${edgeA}% Desventaja`}
            </div>
          </div>

          {/* Team B Edge */}
          <div className="space-y-3" data-id="element-200">
            <div className="text-sm font-medium text-slate-300 border-b border-white/5 pb-2" data-id="element-201">
              {tB}
            </div>
            <div className="flex justify-between text-sm" data-id="element-202">
              <span className="text-slate-400" data-id="element-203">Prob. Calculada:</span>
              <span className="text-white font-medium" data-id="element-204">
                {results.winProbB}%
              </span>
            </div>
            <div className="flex justify-between text-sm" data-id="element-205">
              <span className="text-slate-400" data-id="element-206">Prob. Implícita:</span>
              <span className="text-white font-medium" data-id="element-207">
                {results.impliedProbB}%
              </span>
            </div>
            <div className={`flex items-center gap-1 text-sm font-bold mt-2 pt-2 border-t border-white/5 ${parseFloat(edgeB) > 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-id="element-208">
              {parseFloat(edgeB) > 0 ? <ArrowUpRight className="w-4 h-4" data-id="element-209" /> : <ArrowDownRight className="w-4 h-4" data-id="element-210" />}
              {parseFloat(edgeB) > 0 ? `+${edgeB}% Ventaja` : `${edgeB}% Desventaja`}
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Recommendation Card */}
      {recConfig && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className={`mt-auto rounded-2xl border p-6 flex gap-5 items-start ${recConfig.bg} ${recConfig.border}`} data-id="element-211">
          <div className="shrink-0 mt-1" data-id="element-212">{recConfig.icon}</div>
          <div data-id="element-213">
            <h3 className={`text-xl font-bold mb-2 ${recConfig.color}`} data-id="element-214">
              {recConfig.title}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed" data-id="element-215">
              {recConfig.desc}
            </p>
          </div>
        </motion.div>}
    </div>;
}